/**
 * TURN CC · 31 — Pipeline Health badge for /daily-briefs.
 *
 * Single small pill next to the Next Automated Brief widget.
 * Green : last run success + zero active alerts.
 * Amber : run partial OR any warning alert.
 * Red   : run failed OR any error alert.
 *
 * Click → shadcn Popover with :
 *   - Last run summary (status, articles, duration, email delivery)
 *   - Active alerts (max 5)
 *   - Static "See full history →" pointing to the API endpoint.
 */
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, AlertOctagon, ExternalLink } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API, authHeaders, apiJson } from "@/lib/api";

const POLL_KEY = ["monitoring", "pipeline-health", "product_1_daily_brief"];

function fmtDuration(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m ? `${m}m ${r}s` : `${r}s`;
}

function fmtRelPast(iso) {
  if (!iso) return "—";
  const diffM = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (diffM < 1) return "just now";
  if (diffM < 60) return `${diffM}m ago`;
  if (diffM < 60 * 24) return `${Math.round(diffM / 60)}h ago`;
  return `${Math.round(diffM / 60 / 24)}d ago`;
}

export default function PipelineHealthBadge() {
  const { data } = useQuery({
    queryKey: POLL_KEY,
    queryFn: () => apiJson(`${API}/monitoring/pipeline-health?product_slug=product_1_daily_brief&days=7`,
                          { headers: authHeaders() }),
    staleTime: 60_000,
    gcTime:    24 * 60 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60_000,
  });

  const last = data?.last_run;
  const alerts = last?.alerts || [];
  const activeAlerts = data?.active_alerts || [];
  const hasError = alerts.some((a) => a.level === "error")
                || (last && last.run_status === "failed");
  const hasWarning = alerts.some((a) => a.level === "warning")
                  || (last && last.run_status === "partial");
  const level = hasError ? "error" : hasWarning ? "warning" : "ok";

  const tone =
    level === "error"   ? { bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626", label: "Alert" }
    : level === "warning" ? { bg: "#FEF3C7", fg: "#92400E", dot: "#D97706", label: "Warning" }
    :                       { bg: "#D1FAE5", fg: "#065F46", dot: "#059669", label: "Healthy" };

  const Icon = level === "error" ? AlertOctagon
             : level === "warning" ? AlertTriangle
             : CheckCircle2;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="pipeline-health-badge"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-opacity hover:opacity-85"
          style={{ background: tone.bg, color: tone.fg, border: "none" }}
          type="button"
        >
          <Icon className="h-3.5 w-3.5" style={{ color: tone.dot }} />
          <span>Pipeline · {tone.label}</span>
          {activeAlerts.length > 0 && (
            <span
              style={{
                background: tone.dot, color: "white", borderRadius: 999,
                padding: "0 6px", fontSize: 10, fontWeight: 700,
                marginLeft: 2,
              }}
            >
              {activeAlerts.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end" side="bottom" className="w-96"
        data-testid="pipeline-health-panel"
      >
        {!last && (
          <div className="text-sm text-slate-500 py-6 text-center">
            No pipeline run yet in the last 7 days.
          </div>
        )}
        {last && (
          <div className="space-y-3">
            <div>
              <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-500"
                   style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                Last Run · {fmtRelPast(last.created_at)}
              </div>
              <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 text-sm capitalize">
                  {last.run_status}
                </span>
                <span className="text-slate-400 text-xs">·</span>
                <span className="text-slate-700 text-sm tabular-nums">
                  {last.articles_final ?? "—"} articles
                </span>
                <span className="text-slate-400 text-xs">·</span>
                <span className="text-slate-700 text-sm tabular-nums">
                  {fmtDuration(last.duration_seconds)}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Email: <span className={
                  last.email_status === "sent" ? "text-emerald-700 font-medium"
                  : last.email_status === "failed" ? "text-red-700 font-medium"
                  : ""
                }>{last.email_status || "—"}</span>
                {last.email_recipient && (
                  <> · <span className="text-slate-600">{last.email_recipient}</span></>
                )}
              </div>
            </div>

            {activeAlerts.length > 0 && (
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 mb-2"
                     style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                  Active alerts
                </div>
                <ul className="space-y-1.5" data-testid="pipeline-health-alerts">
                  {activeAlerts.slice(0, 5).map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      <span style={{
                        width: 6, height: 6, marginTop: 6, borderRadius: "50%",
                        background:
                          a.level === "error" ? "#DC2626"
                          : a.level === "warning" ? "#D97706"
                          : "#64748B",
                        flexShrink: 0,
                      }} />
                      <div className="flex-1">
                        <span className="font-mono font-semibold text-slate-800">{a.code}</span>
                        <span className="text-slate-400"> · </span>
                        <span className="text-slate-600">{a.message}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {activeAlerts.length === 0 && (
              <div className="border-t border-slate-100 pt-3 text-xs text-slate-500">
                No active alerts.
              </div>
            )}

            <div className="border-t border-slate-100 pt-3">
              <a
                href={`${API}/monitoring/pipeline-health?product_slug=product_1_daily_brief&days=30`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                See full history <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
